import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	
	Paper,
	Skeleton,
	Tooltip,
	Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ArrowDownwardRounded,
	ArrowUpwardRounded,
	AssignmentTurnedInOutlined,
	EngineeringOutlined,
	SupportAgentOutlined,
	TaskAltOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useCentralAlertas } from "../context";
import {
	
	LABEL_TIPO_EVENTO,
	LABEL_SEVERIDADE,
} from "../../../utils/centralDeAlertas/labels";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

const PAGINA_EVENTOS = 50;

// Ícone de cada tipo de evento (mesmo ícone na linha e nos chips — chips
// sempre com ícone + rótulo, nunca cor sozinha).
const ICONE_TIPO = {
	queda: ArrowDownwardRounded,
	recuperacao: ArrowUpwardRounded,
	atendimento_aberto: SupportAgentOutlined,
	atendimento_fechado: TaskAltOutlined,
	os_criada: EngineeringOutlined,
	os_concluida: AssignmentTurnedInOutlined,
};

// Feed cronológico de todos os eventos do período (mais recente primeiro),
// agrupado por dia com cabeçalho sticky, filtrável por tipo de evento e com
// paginação incremental. Clicar num evento vinculado a incidente foca a
// linha correspondente na TabelaIncidentes (focarIncidente do context).
const TimelineGlobal = () => {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { agora, carregando, eventosFiltrados, focarIncidente } =
		useCentralAlertas();

	const [limite, setLimite] = useState(PAGINA_EVENTOS);

	// Reinicia a paginação quando os eventos mudam.
	useEffect(() => {
		setLimite(PAGINA_EVENTOS);
	}, [eventosFiltrados]);

	const eventosExibidos = useMemo(
		() => eventosFiltrados.slice(0, limite),
		[eventosFiltrados, limite]
	);

	// Agrupa os eventos exibidos por dia, preservando a ordem (desc).
	const grupos = useMemo(() => {
		const rotuloDia = (dataHora) => {
			const data = dayjs(dataHora);
			if (data.isSame(agora, "day")) return "Hoje";
			if (data.isSame(agora.subtract(1, "day"), "day")) return "Ontem";
			return data.format("DD [de] MMMM");
		};

		const lista = [];
		let grupoAtual = null;
		eventosExibidos.forEach((evento) => {
			const chave = dayjs(evento.dataHora).format("YYYY-MM-DD");
			if (!grupoAtual || grupoAtual.chave !== chave) {
				grupoAtual = { chave, rotulo: rotuloDia(evento.dataHora), eventos: [] };
				lista.push(grupoAtual);
			}
			grupoAtual.eventos.push(evento);
		});
		return lista;
	}, [eventosExibidos, agora]);

	const haMais = eventosFiltrados.length > eventosExibidos.length;

	// Linha compacta: ícone do tipo, hora, "tipo · equipamento" e ponto de
	// severidade — cliente e severidade completos ficam no Tooltip.
	const renderEvento = (evento) => {
		const clicavel = Boolean(evento.incidenteId);
		const IconeEvento = ICONE_TIPO[evento.tipo];
		const detalhe = [
			evento.clienteNome,
			evento.severidade != null ? LABEL_SEVERIDADE[evento.severidade] : null,
			clicavel ? "clique para ver na tabela" : null,
		]
			.filter(Boolean)
			.join(" · ");

		return (
			<Tooltip key={evento.id} title={detalhe} arrow placement="left">
				<Box
					sx={styles.linhaEvento(clicavel)}
					onClick={
						clicavel ? () => focarIncidente(evento.incidenteId) : undefined
					}
				>
					{IconeEvento && <IconeEvento sx={styles.iconeEvento(evento.tipo)} />}
					<Typography variant="caption" sx={styles.hora}>
						{dayjs(evento.dataHora).format("HH:mm")}
					</Typography>
					<Typography variant="caption" sx={styles.textoOrigem}>
						<Box component="span" sx={styles.textoTipo}>
							{LABEL_TIPO_EVENTO[evento.tipo]}
						</Box>
						{` · ${evento.equipamentoNome || "—"}`}
					</Typography>
					<Box sx={styles.pontoSeveridade(evento.severidade)} />
				</Box>
			</Tooltip>
		);
	};

	const renderConteudo = () => {
		if (carregando) {
			return (
				<>
					
					<Box sx={styles.listaEventos}>
						{[0, 1, 2, 3, 4, 5, 6, 7].map((indice) => (
							<Box key={indice} sx={styles.skeletonLinha}>
								<Skeleton variant="circular" width={18} height={18} />
								<Skeleton variant="text" width={38} />
								<Skeleton variant="text" sx={styles.skeletonTextos} />
							</Box>
						))}
					</Box>
				</>
			);
		}

		return (
			<>
				{!eventosFiltrados.length ? (
					<Box sx={styles.estadoVazio}>
						<Typography color="text.secondary">
							Nenhum evento no período selecionado.
						</Typography>
					</Box>
				) : (
					<Box sx={styles.listaEventos}>
						{grupos.map((grupo) => (
							<Box key={grupo.chave}>
								<Box sx={styles.cabecalhoDia}>
									<Typography
										variant="caption"
										sx={styles.textoCabecalhoDia}
									>
										{grupo.rotulo}
									</Typography>
								</Box>
								{grupo.eventos.map(renderEvento)}
							</Box>
						))}
						{haMais && (
							<Box sx={styles.rodapePaginacao}>
								<Typography variant="caption" sx={styles.textoPaginacao}>
									{`Exibindo ${eventosExibidos.length} de ${eventosFiltrados.length} eventos`}
								</Typography>
								<Button
									size="small"
									sx={styles.botaoCarregarMais}
									onClick={() =>
										setLimite((atual) => atual + PAGINA_EVENTOS)
									}
								>
									Carregar mais
								</Button>
							</Box>
						)}
					</Box>
				)}
			</>
		);
	};

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Typography variant="mySubtitle">Eventos recentes</Typography>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.timelineGlobal} />
			</Box>
			{renderConteudo()}
		</Paper>
	);
};

export default TimelineGlobal;
